import { useCallback, useRef, useState } from "react";
import { load } from "@2gis/mapgl";
import { getMapStyle } from "../../utils/map/mapStyles";
import { MapInstance, MapGL } from "@/types/map";

export const useMapInitialization = () => {
    const [isMapReady, setIsMapReady] = useState(false);
    const [currentMapStyle, setCurrentMapStyle] = useState<string | null>(null);

    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<MapInstance | null>(null);
    const mapglRef = useRef<MapGL | null>(null);
    const currentCityIdRef = useRef<number | null>(null);
    const isInitializingRef = useRef(false);

    const initMap = useCallback(async (
        selectedCity: any,
        mapToken: string,
        isTrafficVisible: boolean,
        theme: "light" | "dark",
        clearAllMarkers: () => void,
        events?: any[],
        webcams?: any[],
        onMapReady?: () => void,
        initialZoom?: number
    ) => {
        console.log("🔄 initMap вызван:", {
            selectedCity: selectedCity?.name,
            cityId: selectedCity?.id,
            currentCityId: currentCityIdRef.current,
            isInitializing: isInitializingRef.current,
            hasMap: !!mapInstanceRef.current,
        });

        if (!selectedCity || !mapRef.current || isInitializingRef.current) return;

        // Если карта есть, но другой город - центрируем и очищаем маркеры
        if (mapInstanceRef.current && currentCityIdRef.current !== selectedCity.id) {
            console.log("🗺️ Центрируем существующую карту на новый город:", selectedCity.name);

            clearAllMarkers();

            mapInstanceRef.current.setCenter([selectedCity.longitude, selectedCity.latitude]);
            mapInstanceRef.current.setZoom(initialZoom ?? 12);

            currentCityIdRef.current = selectedCity.id;
            return;
        }

        if (mapInstanceRef.current && currentCityIdRef.current === selectedCity.id) {
            console.log("Карта уже создана для города:", selectedCity.name);
            return;
        }

        isInitializingRef.current = true;

        try {
            console.log("🗺️ Инициализация карты для города:", selectedCity.name);

            if (mapInstanceRef.current) {
                clearAllMarkers();
                setIsMapReady(false);
                mapInstanceRef.current.destroy();
                mapInstanceRef.current = null;
            }

            if (!mapglRef.current) {
                console.log("📡 Загрузка 2GIS API...");
                // Приводим к типу any, а затем к MapGL для обхода проблемы с типизацией
                const loadedMapgl = await load();
                mapglRef.current = loadedMapgl as unknown as MapGL;
            }

            const mapStyle = getMapStyle(theme);

            mapInstanceRef.current = new mapglRef.current.Map(mapRef.current, {
                center: [selectedCity.longitude, selectedCity.latitude],
                zoom: initialZoom ?? 13,
                key: mapToken,
                rotationControl: false,
                zoomControl: false,
                trafficControl: false,
                copyright: false,
                styleState: {
                    trafficOn: isTrafficVisible,
                },
                style: mapStyle,
            });

            currentCityIdRef.current = selectedCity.id;
            setCurrentMapStyle(mapStyle);

            mapInstanceRef.current.once("styleload", () => {
                console.log("✅ Карта готова для города:", selectedCity.name);
                setIsMapReady(true);

                if (isTrafficVisible && mapInstanceRef.current) {
                    try {
                        mapInstanceRef.current.showTraffic();
                    } catch (error) {
                        console.warn("Ошибка включения трафика:", error);
                    }
                }

                if (
                    (!events || events.length === 0) &&
                    (!webcams || webcams.length === 0)
                ) {
                    console.log("🗺️ Карта готова, нет маркеров для добавления");
                    if (onMapReady) {
                        onMapReady();
                    }
                }

                console.log("📍 Карта готова, маркеры добавятся через useEffect");
            });
        } catch (err) {
            console.error("❌ Ошибка при инициализации карты:", err);
        } finally {
            isInitializingRef.current = false;
        }
    }, []);

    const updateMapStyle = useCallback((theme: "light" | "dark") => {
        if (mapInstanceRef.current && isMapReady) {
            const newStyle = getMapStyle(theme);

            if (currentMapStyle !== newStyle) {
                console.log("🔄 Обновляем стиль карты с", currentMapStyle, "на", newStyle);

                try {
                    if (mapInstanceRef.current.setStyle) {
                        mapInstanceRef.current.setStyle(newStyle);
                        setCurrentMapStyle(newStyle);
                        console.log("✅ Стиль карты обновлен через setStyle");
                        return true;
                    }
                } catch (error) {
                    console.warn("Ошибка изменения стиля карты:", error);
                }
            }
        }
        return false;
    }, [currentMapStyle, isMapReady]);

    const destroyMap = useCallback(() => {
        if (mapInstanceRef.current) {
            console.log("🧹 Очистка карты при размонтировании");
            setIsMapReady(false);
            mapInstanceRef.current.destroy();
            mapInstanceRef.current = null;
            currentCityIdRef.current = null;
            isInitializingRef.current = false;
        }
    }, []);

    return {
        mapRef,
        isMapReady,
        currentMapStyle,
        mapInstance: mapInstanceRef.current,
        mapgl: mapglRef.current,
        currentCityId: currentCityIdRef.current,
        initMap,
        updateMapStyle,
        destroyMap,
        setIsMapReady,
        setCurrentMapStyle,
    };
};
